import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  CircularProgress,
  IconButton,
  Tooltip,
  Divider,
  Card,
  CardContent,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Download as DownloadIcon,
  Payment as PaymentIcon,
  Delete as DeleteIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { api, firmAPI } from '../services/api';

interface ClientInvoice {
  id: number;
  client_firm_id: number;
  invoice_number: string;
  invoice_date: string;
  period_start: string;
  period_end: string;
  opening_balance: number;
  total_billing: number;
  total_payments: number;
  net_amount: number;
  currency: string;
  status: 'draft' | 'submitted';
  notes: string;
  total_paid: number;
  remaining_balance: number;
  payment_status: 'unpaid' | 'partial' | 'paid';
  clientFirm?: {
    id: number;
    name: string;
    category?: 'prepaid' | 'postpaid';
    address_1?: string;
    address_2?: string;
    city?: string;
    state?: string;
    zip_code?: string;
    country?: string;
  };
  createdBy?: {
    id: number;
    name: string;
  };
  items: ClientInvoiceItem[];
}

interface ClientInvoiceItem {
  id: number;
  client_estimate_id?: number;
  case_title: string;
  billing_type: string;
  rate: number;
  quantity: number;
  amount: number;
  description: string;
    case?: {
    id: number;
    title: string;
    project_id: number;
    project?: {
      id: number;
      settings?: {
        id: number;
        project_id: number;
        custom_fields?: {
          billing_type?: string;
          flat_rate?: number;
          flat_rate_page_to?: number;
          variable_rate?: number;
          additional_claimant?: number;
        };
      };
    };
  };
  clientEstimate?: {
    id: number;
    additional_claimant?: boolean;
  } | null;
}

interface Payment {
  id: number;
  payment_date: string;
  payment_type: string;
  reference_number: string;
  amount: number;
  notes: string;
  recordedBy?: {
    id: number;
    name: string;
  };
}

interface WalletAllocation {
  id: number;
  created_at: string;
  type: string;
  amount: number;
  description: string;
  client_estimate_id: number;
  createdBy?: {
    id: number;
    name: string;
  };
}

const ClientInvoiceView: React.FC = () => {
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const navigate = useNavigate();
  
  const [invoice, setInvoice] = useState<ClientInvoice | null>(null);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [walletAllocations, setWalletAllocations] = useState<WalletAllocation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    payment_date: new Date().toISOString().split('T')[0],
    payment_type: 'check',
    reference_number: '',
    amount: '',
    notes: '',
  });
  const [paymentLoading, setPaymentLoading] = useState(false);

  useEffect(() => {
    if (invoiceId) {
      loadInvoiceDetails();
    }
  }, [invoiceId]);

  // Load payments/allocations after invoice is loaded
  useEffect(() => {
    if (invoice) {
      loadPayments();
    }
  }, [invoice]);

  // Check if client is prepaid
  const isPrepaid = invoice?.clientFirm?.category === 'prepaid';

  const loadInvoiceDetails = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/client-invoices/${invoiceId}`);
      if (response.data.success) {
        const invoiceData = response.data.data;
        // Handle both snake_case and camelCase formats
        if (!invoiceData.clientFirm && invoiceData.client_firm) {
          invoiceData.clientFirm = invoiceData.client_firm;
        }
        // If clientFirm is still missing but we have client_firm_id, fetch it
        if (!invoiceData.clientFirm && invoiceData.client_firm_id) {
          try {
            const firmResponse = await api.get(`/firms/${invoiceData.client_firm_id}`);
            if (firmResponse.data.success) {
              invoiceData.clientFirm = firmResponse.data.data;
            }
          } catch (firmErr) {
            console.error('Failed to load client firm:', firmErr);
          }
        }
        setInvoice(invoiceData);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load invoice details');
    } finally {
      setLoading(false);
    }
  };

  // Load payments/allocations after invoice is loaded
  useEffect(() => {
    if (invoice) {
      loadPayments();
    }
  }, [invoice]);

  const loadPayments = async () => {
    if (!invoice) return;
    
    try {
      // For prepaid clients, load wallet allocations instead of payments
      if (invoice.clientFirm?.category === 'prepaid' && invoice.clientFirm?.id) {
        // Get estimate IDs from invoice items
        const estimateIds = (invoice.items || [])
          .map((item: any) => item.client_estimate_id)
          .filter((id: any) => id != null);
        
        if (estimateIds.length > 0) {
          // Fetch wallet transactions and filter for allocations related to these estimates
          const transactionsResponse = await firmAPI.getWalletTransactions(invoice.clientFirm.id, { per_page: 100 });
          if (transactionsResponse.data.success) {
            const allTransactions = transactionsResponse.data.data.data || transactionsResponse.data.data || [];
            // Filter for allocation type transactions that match our estimate IDs
            const allocations = allTransactions.filter((tx: any) => 
              tx.type === 'allocation' && 
              tx.client_estimate_id && 
              estimateIds.includes(tx.client_estimate_id)
            );
            setWalletAllocations(allocations);
          }
        } else {
          setWalletAllocations([]);
        }
      } else {
        // For postpaid clients, load regular payments
        const response = await api.get(`/client-invoices/${invoiceId}/payments`);
        if (response.data.success) {
          setPayments(response.data.data);
        }
      }
    } catch (err: any) {
      console.error('Failed to load payments/allocations:', err);
    }
  };

  const handleDownloadPdf = async () => {
    try {
      const response = await api.get(`/client-invoices/${invoiceId}/download`, {
        responseType: 'blob',
      });
      
      // Check if response is actually a PDF blob or an error JSON
      const contentType = response.headers['content-type'] || '';
      if (contentType.includes('application/json')) {
        // Response is JSON error, parse it
        const text = await new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.readAsText(response.data);
        });
        const errorData = JSON.parse(text);
        setError(errorData.message || 'Failed to download invoice PDF');
        return;
      }
      
      // Create download link for PDF
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `client-invoice-${invoice?.invoice_number || invoiceId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      // Try to extract error message from blob response
      if (err.response?.data instanceof Blob) {
        try {
          const text = await new Promise<string>((resolve) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.readAsText(err.response.data);
          });
          const errorData = JSON.parse(text);
          setError(errorData.message || 'Failed to download invoice PDF');
        } catch (parseErr) {
          setError('Failed to download invoice PDF');
        }
      } else {
        setError(err.response?.data?.message || 'Failed to download invoice PDF');
      }
    }
  };

  const handleSubmitInvoice = async () => {
    if (!window.confirm('Are you sure you want to submit this invoice to the client?')) {
      return;
    }

    try {
      const response = await api.post(`/client-invoices/${invoiceId}/submit`);
      if (response.data.success) {
        setSuccess('Invoice submitted to client successfully');
        loadInvoiceDetails();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to submit invoice');
    }
  };

  const handleRecordPayment = async () => {
    if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) {
      setError('Please enter a valid payment amount');
      return;
    }

    try {
      setPaymentLoading(true);
      const response = await api.post(`/client-invoices/${invoiceId}/payments`, {
        ...paymentForm,
        amount: parseFloat(paymentForm.amount),
      });

      if (response.data.success) {
        setSuccess('Payment recorded successfully');
        setShowPaymentDialog(false);
        setPaymentForm({
          payment_date: new Date().toISOString().split('T')[0],
          payment_type: 'check',
          reference_number: '',
          amount: '',
          notes: '',
        });
        loadInvoiceDetails();
        loadPayments();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to record payment');
    } finally {
      setPaymentLoading(false);
    }
  };

  const handleDeletePayment = async (paymentId: number) => {
    if (!window.confirm('Are you sure you want to delete this payment?')) {
      return;
    }

    try {
      const response = await api.delete(`/client-invoices/${invoiceId}/payments/${paymentId}`);
      if (response.data.success) {
        setSuccess('Payment deleted successfully');
        loadInvoiceDetails();
        loadPayments();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete payment');
    }
  };

  const handleRecalculatePrepaidBalance = async () => {
    if (!window.confirm('Recalculate opening balance and utilized balance from wallet allocations?')) {
      return;
    }

    try {
      setLoading(true);
      const response = await api.post(`/client-invoices/${invoiceId}/recalculate-prepaid`);
      if (response.data.success) {
        setSuccess('Balance recalculated successfully');
        loadInvoiceDetails();
        loadPayments();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to recalculate balance');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      draft: 'default',
      submitted: 'success',
    };
    return colors[status] || 'default';
  };

  const getPaymentStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      unpaid: 'error',
      partial: 'warning',
      paid: 'success',
    };
    return colors[status] || 'default';
  };

  const getBillingTypeLabel = (type: string) => {
    if (!type) return 'N/A';
    // Convert snake_case to Title Case
    return type
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
        <Button sx={{ mt: 2 }} onClick={() => navigate('/client-invoices')}>
          Back to Invoices
        </Button>
      </Box>
    );
  }

  if (!invoice) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Invoice not found</Alert>
        <Button sx={{ mt: 2 }} onClick={() => navigate('/client-invoices')}>
          Back to Invoices
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate('/client-invoices')}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4">
            Invoice {invoice.invoice_number}
          </Typography>
          <Chip
            label={invoice.status.toUpperCase()}
            color={getStatusColor(invoice.status) as any}
          />
          <Chip
            label={invoice.payment_status.toUpperCase()}
            color={getPaymentStatusColor(invoice.payment_status) as any}
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadPdf}
          >
            Download PDF
          </Button>
          {invoice.status === 'draft' && (
            <Button
              variant="contained"
              startIcon={<SendIcon />}
              onClick={handleSubmitInvoice}
            >
              Submit to Client
            </Button>
          )}
          {/* Show Recalculate button for prepaid clients with draft invoices if balance seems incorrect */}
          {isPrepaid && invoice.status === 'draft' && invoice.opening_balance === 0 && invoice.total_payments === 0 && (
            <Button
              variant="outlined"
              color="warning"
              onClick={handleRecalculatePrepaidBalance}
              disabled={loading}
            >
              Recalculate Balance
            </Button>
          )}
          {/* Only show Record Payment button for postpaid clients */}
          {!isPrepaid && (
            <Button
              variant="contained"
              startIcon={<PaymentIcon />}
              onClick={() => setShowPaymentDialog(true)}
            >
              Record Payment
            </Button>
          )}
        </Box>
      </Box>

      {/* Warning for prepaid clients with incorrect balance */}
      {isPrepaid && invoice.status === 'draft' && invoice.opening_balance === 0 && invoice.total_payments === 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body2" gutterBottom>
            <strong>Balance needs recalculation:</strong> Opening balance and utilized balance are showing as $0.00. 
            This invoice may have been created before the prepaid logic was implemented.
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={handleRecalculatePrepaidBalance}
            disabled={loading}
            sx={{ mt: 1 }}
          >
            Recalculate from Wallet
          </Button>
        </Alert>
      )}

      {/* Invoice Summary */}
      <Box sx={{ display: 'flex', gap: 3, mb: 3, flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1, minWidth: 250 }}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Total Amount
              </Typography>
              <Typography variant="h4">
                ${Number(invoice.net_amount).toFixed(2)}
              </Typography>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: 1, minWidth: 250 }}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Total Received
              </Typography>
              <Typography variant="h4" color="success.main">
                ${Number(invoice.total_paid).toFixed(2)}
              </Typography>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: 1, minWidth: 250 }}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Remaining Balance
              </Typography>
              <Typography variant="h4" color="error.main">
                ${Number(invoice.remaining_balance).toFixed(2)}
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Invoice Details */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Invoice Details
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ flex: 1, minWidth: 300 }}>
            <Typography variant="subtitle2" color="text.secondary">Client Firm</Typography>
            <Typography variant="body1">{invoice.clientFirm?.name || 'N/A'}</Typography>
            {invoice.clientFirm?.address_1 && (
              <Typography variant="body2" color="text.secondary">
                {invoice.clientFirm.address_1}<br />
                {invoice.clientFirm.address_2 && <>{invoice.clientFirm.address_2}<br /></>}
                {invoice.clientFirm.city && invoice.clientFirm.state && invoice.clientFirm.zip_code && (
                  <>{invoice.clientFirm.city}, {invoice.clientFirm.state} {invoice.clientFirm.zip_code}<br /></>
                )}
                {invoice.clientFirm.country}
              </Typography>
            )}
          </Box>
          <Box sx={{ flex: 1, minWidth: 300 }}>
            <Typography variant="subtitle2" color="text.secondary">Invoice Date</Typography>
            <Typography variant="body1">
              {new Date(invoice.invoice_date).toLocaleDateString()}
            </Typography>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2 }}>
              Billing Period
            </Typography>
            <Typography variant="body1">
              {new Date(invoice.period_start).toLocaleDateString()} - {new Date(invoice.period_end).toLocaleDateString()}
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Invoice Items */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Invoice Items
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Case Title</TableCell>
                <TableCell>Billing Type</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="right">Rate</TableCell>
                <TableCell align="right">Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invoice.items && invoice.items.length > 0 ? invoice.items.map((item) => {
                // Check if this is a flat rate case and needs breakdown
                const isFlatRate = item.billing_type === 'flat_rate';
                const projectSettings = item.case?.project?.settings;
                const customFields = projectSettings?.custom_fields;
                const showBreakdown = isFlatRate && 
                  customFields?.billing_type === 'Flat Rate' && 
                  customFields?.flat_rate_page_to !== null && 
                  item.quantity > (customFields.flat_rate_page_to || 0);

                if (showBreakdown) {
                  const flatRate = customFields.flat_rate || 0;
                  const flatRatePageTo = customFields.flat_rate_page_to || 0;
                  const variableRate = customFields.variable_rate || 0;
                  const pagesAboveFlatRate = item.quantity - flatRatePageTo;
                  const flatRateAmount = flatRate;
                  const variableAmount = variableRate * pagesAboveFlatRate;
                  const additionalClaimantAmount = Number(customFields.additional_claimant ?? 0);
                  const showAdditionalClaimant = Boolean(item.clientEstimate?.additional_claimant) && additionalClaimantAmount > 0;
                  const rowSpan = 3 + (showAdditionalClaimant ? 1 : 0);

                  return (
                    <React.Fragment key={item.id}>
                      {/* First row: Case title and flat rate portion */}
                      <TableRow>
                        <TableCell rowSpan={rowSpan} sx={{ verticalAlign: 'top', fontWeight: 'medium' }}>
                          {item.case_title}
                        </TableCell>
                        <TableCell>{getBillingTypeLabel(item.billing_type)}</TableCell>
                        <TableCell>
                          Pages 1-{flatRatePageTo} (Flat Rate)
                        </TableCell>
                        <TableCell align="right">${Number(flatRate).toFixed(2)}</TableCell>
                        <TableCell align="right">${Number(flatRateAmount).toFixed(2)}</TableCell>
                      </TableRow>
                      {/* Second row: Variable rate portion */}
                      <TableRow>
                        <TableCell>{getBillingTypeLabel(item.billing_type)}</TableCell>
                        <TableCell>
                          Pages {flatRatePageTo + 1}-{item.quantity} ({pagesAboveFlatRate} × ${Number(variableRate).toFixed(2)})
                        </TableCell>
                        <TableCell align="right">${Number(variableRate).toFixed(2)}</TableCell>
                        <TableCell align="right">${Number(variableAmount).toFixed(2)}</TableCell>
                      </TableRow>
                      {/* Optional third row: Additional Claimant */}
                      {showAdditionalClaimant && (
                        <TableRow>
                          <TableCell>{getBillingTypeLabel(item.billing_type)}</TableCell>
                          <TableCell>Additional Claimant</TableCell>
                          <TableCell align="right">${Number(additionalClaimantAmount).toFixed(2)}</TableCell>
                          <TableCell align="right">${Number(additionalClaimantAmount).toFixed(2)}</TableCell>
                        </TableRow>
                      )}
                      {/* Subtotal row */}
                      <TableRow sx={{ backgroundColor: 'rgba(0, 0, 0, 0.04)' }}>
                        <TableCell colSpan={2} align="right" sx={{ fontWeight: 'bold' }}>
                          Subtotal:
                        </TableCell>
                        <TableCell></TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                          ${Number(item.amount).toFixed(2)}
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                } else if (isFlatRate && customFields?.billing_type === 'Flat Rate') {
                  // Within flat rate range - show one or two rows (second row if Additional Claimant)
                  const additionalClaimantAmount = Number(customFields.additional_claimant ?? 0);
                  const showAdditionalClaimant = Boolean(item.clientEstimate?.additional_claimant) && additionalClaimantAmount > 0;

                  if (showAdditionalClaimant) {
                    const flatAmount = Number(item.amount) - additionalClaimantAmount;
                    return (
                      <React.Fragment key={item.id}>
                        <TableRow>
                          <TableCell rowSpan={2} sx={{ verticalAlign: 'top', fontWeight: 'medium' }}>{item.case_title}</TableCell>
                          <TableCell>{getBillingTypeLabel(item.billing_type)}</TableCell>
                          <TableCell>Pages 1-{item.quantity} (Flat Rate)</TableCell>
                          <TableCell align="right">${Number(flatAmount).toFixed(2)}</TableCell>
                          <TableCell align="right">${Number(flatAmount).toFixed(2)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>{getBillingTypeLabel(item.billing_type)}</TableCell>
                          <TableCell>Additional Claimant</TableCell>
                          <TableCell align="right">${Number(additionalClaimantAmount).toFixed(2)}</TableCell>
                          <TableCell align="right">${Number(additionalClaimantAmount).toFixed(2)}</TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  }
                  return (
                    <TableRow key={item.id}>
                      <TableCell sx={{ fontWeight: 'medium' }}>{item.case_title}</TableCell>
                      <TableCell>{getBillingTypeLabel(item.billing_type)}</TableCell>
                      <TableCell>
                        Pages 1-{item.quantity} (Flat Rate)
                      </TableCell>
                      <TableCell align="right">${Number(item.rate).toFixed(2)}</TableCell>
                      <TableCell align="right">${Number(item.amount).toFixed(2)}</TableCell>
                    </TableRow>
                  );
                } else {
                  // Not a flat rate case - show normal row
                  // For time-based, show hours instead of quantity
                  const isTimeBased = item.billing_type === 'time_based';
                  const quantityDisplay = isTimeBased 
                    ? `${Number(item.quantity).toFixed(4)} hrs`
                    : `Qty: ${item.quantity}`;
                  
                  return (
                    <TableRow key={item.id}>
                      <TableCell sx={{ fontWeight: 'medium' }}>{item.case_title}</TableCell>
                      <TableCell>
                        {getBillingTypeLabel(item.billing_type)}
                      </TableCell>
                      <TableCell>
                        {item.description || quantityDisplay}
                      </TableCell>
                      <TableCell align="right">
                        {isTimeBased ? `$${Number(item.rate).toFixed(2)}/hr` : `$${Number(item.rate).toFixed(2)}`}
                      </TableCell>
                      <TableCell align="right">${Number(item.amount).toFixed(2)}</TableCell>
                    </TableRow>
                  );
                }
              }) : (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    No invoice items found.
                  </TableCell>
                </TableRow>
              )}
              <TableRow>
                <TableCell colSpan={4} align="right"><strong>Opening Balance:</strong></TableCell>
                <TableCell align="right"><strong>${Number(invoice.opening_balance).toFixed(2)}</strong></TableCell>
              </TableRow>
              <TableRow>
                <TableCell colSpan={4} align="right"><strong>Current Billing:</strong></TableCell>
                <TableCell align="right"><strong>${Number(invoice.total_billing).toFixed(2)}</strong></TableCell>
              </TableRow>
              <TableRow>
                <TableCell colSpan={4} align="right">
                  <strong>{isPrepaid ? 'Utilized Balance:' : 'Total Payments:'}</strong>
                </TableCell>
                <TableCell align="right">
                  <strong>
                    {isPrepaid 
                      ? `$${Number(invoice.total_payments).toFixed(2)}`
                      : `$${Number(invoice.total_payments).toFixed(2)}`
                    }
                  </strong>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell colSpan={4} align="right">
                  <Typography variant="h6">Net Amount:</Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="h6">${Number(invoice.net_amount).toFixed(2)}</Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Payment History / Wallet Allocations */}
      <Paper elevation={2} sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          {isPrepaid ? 'Wallet Utilization (Ledger)' : 'Payment History'}
        </Typography>
        <Divider sx={{ mb: 2 }} />
        {isPrepaid ? (
          // Show wallet allocations for prepaid clients
          walletAllocations.length === 0 ? (
            <Typography color="text.secondary">No wallet allocations for this invoice period.</Typography>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Transaction Type</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Created By</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {walletAllocations.map((allocation) => (
                    <TableRow key={allocation.id}>
                      <TableCell>
                        {new Date(allocation.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>Wallet Allocation</TableCell>
                      <TableCell>{allocation.description || '-'}</TableCell>
                      <TableCell>${Number(allocation.amount).toFixed(2)}</TableCell>
                      <TableCell>{allocation.createdBy?.name || 'N/A'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )
        ) : (
          // Show regular payments for postpaid clients
          payments.length === 0 ? (
            <Typography color="text.secondary">No payments recorded yet.</Typography>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Payment Type</TableCell>
                    <TableCell>Reference Number</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Recorded By</TableCell>
                    <TableCell>Notes</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {payments.map((payment) => (
                    <TableRow key={payment.id}>
                      <TableCell>
                        {new Date(payment.payment_date).toLocaleDateString()}
                      </TableCell>
                      <TableCell>{payment.payment_type}</TableCell>
                      <TableCell>{payment.reference_number || '-'}</TableCell>
                      <TableCell>${Number(payment.amount).toFixed(2)}</TableCell>
                      <TableCell>{payment.recordedBy?.name || 'N/A'}</TableCell>
                      <TableCell>{payment.notes || '-'}</TableCell>
                      <TableCell>
                        <Tooltip title="Delete Payment">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeletePayment(payment.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )
        )}
      </Paper>

      {/* Record Payment Dialog */}
      <Dialog
        open={showPaymentDialog}
        onClose={() => !paymentLoading && setShowPaymentDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Record Payment</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Payment Date"
            type="date"
            value={paymentForm.payment_date}
            onChange={(e) => setPaymentForm({ ...paymentForm, payment_date: e.target.value })}
            margin="normal"
            InputLabelProps={{ shrink: true }}
            required
          />

          <FormControl fullWidth margin="normal">
            <InputLabel>Payment Type</InputLabel>
            <Select
              value={paymentForm.payment_type}
              onChange={(e) => setPaymentForm({ ...paymentForm, payment_type: e.target.value })}
              label="Payment Type"
            >
              <MenuItem value="check">Check</MenuItem>
              <MenuItem value="wire">Wire Transfer</MenuItem>
              <MenuItem value="ach">ACH</MenuItem>
              <MenuItem value="credit_card">Credit Card</MenuItem>
              <MenuItem value="cash">Cash</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Reference Number"
            value={paymentForm.reference_number}
            onChange={(e) => setPaymentForm({ ...paymentForm, reference_number: e.target.value })}
            margin="normal"
            helperText="Check number, transaction ID, etc."
          />

          <TextField
            fullWidth
            label="Amount"
            type="number"
            value={paymentForm.amount}
            onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
            margin="normal"
            required
            inputProps={{ step: '0.01', min: '0.01', max: invoice.remaining_balance }}
            helperText={`Remaining balance: $${Number(invoice.remaining_balance).toFixed(2)}`}
          />

          <TextField
            fullWidth
            label="Notes"
            multiline
            rows={3}
            value={paymentForm.notes}
            onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPaymentDialog(false)} disabled={paymentLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleRecordPayment}
            variant="contained"
            disabled={paymentLoading}
          >
            {paymentLoading ? <CircularProgress size={24} /> : 'Record Payment'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
      >
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ClientInvoiceView;

